import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { Button, Card, ErrorText, Field, Input, PageHeader, Pill, Select } from '../components/ui';
import { NotificationSettings } from '../components/NotificationSettings';
import { ChangePassword } from './Password';

export function Settings() {
  const me = useMe();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  const [form, setForm] = useState({ timezone: '', quietHoursEnabled: false, quietHoursStart: '23:00', quietHoursEnd: '07:00' });
  useEffect(() => {
    if (me.data) setForm({ timezone: me.data.timezone, quietHoursEnabled: me.data.quietHours.enabled, quietHoursStart: me.data.quietHours.start ?? '23:00', quietHoursEnd: me.data.quietHours.end ?? '07:00' });
  }, [me.data]);
  const save = useMutation({ mutationFn: () => api('/me', { method: 'PATCH', body: form }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }) });
  const link = useMutation({ mutationFn: () => api<{ url: string | null; configured: boolean; code: string }>('/me/telegram-link', { method: 'POST' }) });
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => api<{ id: string; userAgent: string | null; ip: string | null; createdAt: string; lastUsedAt: string }[]>('/auth/sessions') });

  return (
    <>
      <PageHeader title="Settings" sub={me.data ? `${me.data.name} · ${me.data.email}` : ''} />
      <div className="grid gap-3.5 lg:grid-cols-2">
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">Time and quiet hours</h2>
          <Field label="Your timezone (IANA name)" hint="All times in the app are shown in this zone.">
            <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          </Field>
          <label className="flex cursor-pointer items-center gap-2.5">
            <input type="checkbox" className="h-[18px] w-[18px]" checked={form.quietHoursEnabled} onChange={(e) => setForm({ ...form, quietHoursEnabled: e.target.checked })} />
            <span>Use quiet hours (non-critical alerts wait; report deadlines move to the end)</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><Input type="time" value={form.quietHoursStart} onChange={(e) => setForm({ ...form, quietHoursStart: e.target.value })} /></Field>
            <Field label="Until"><Input type="time" value={form.quietHoursEnd} onChange={(e) => setForm({ ...form, quietHoursEnd: e.target.value })} /></Field>
          </div>
          <ErrorText error={save.error ? errorText(save.error) : null} />
          {save.isSuccess && <p className="m-0 text-sm text-up">Saved.</p>}
          <Button variant="primary" className="self-start" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
        </Card>

        <Card className="flex flex-col gap-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="m-0 text-[15px] font-semibold">Telegram alerts</h2>
            {me.data && <Pill tone={me.data.telegramLinked ? 'up' : 'neutral'}>{me.data.telegramLinked ? 'Linked' : 'Not linked'}</Pill>}
          </div>
          <p className="m-0 text-sm text-sub">Get report reminders, approval requests and risk alerts on Telegram. Critical alerts always reach you.</p>
          <Button className="self-start" onClick={() => link.mutate()} disabled={link.isPending}>Get my link</Button>
          {link.data && (link.data.url ? <a href={link.data.url} target="_blank" rel="noreferrer" className="font-semibold">Open Telegram and tap Start →</a> : <p className="m-0 text-sm text-warn">The Telegram bot isn't configured on this server yet. Ask an admin.</p>)}
          <ErrorText error={link.error ? errorText(link.error) : null} />
        </Card>

        <ReportingCurrency />

        {me.data && <ChangePassword totpEnabled={me.data.totpEnabled} />}

        <NotificationSettings />

        <Card className="flex flex-col gap-2 p-4 lg:col-span-2">
          <h2 className="m-0 text-[15px] font-semibold">Signed-in devices</h2>
          {sessions.data?.map((s) => (
            <div key={s.id} className="flex justify-between gap-3 rounded-lg bg-raised px-3 py-2 text-sm">
              <span className="truncate text-ink2">{s.userAgent ?? 'Unknown device'}</span>
              <span className="whitespace-nowrap text-muted">last used {fmtTime(s.lastUsedAt, tz, true)}</span>
            </div>
          ))}
        </Card>
      </div>
    </>
  );
}

/** D10: the currency my risk desk and Today totals open in. */
function ReportingCurrency() {
  const qc = useQueryClient();
  const cur = useQuery({ queryKey: ['reporting-currency'], queryFn: () => api<{ currencies: string[]; reportingCurrency: string; effective: string }>('/me/reporting-currency') });
  const [pick, setPick] = useState<string | null>(null);
  const value = pick ?? cur.data?.effective ?? '';
  const save = useMutation({
    mutationFn: () => api('/me/reporting-currency', { method: 'PUT', body: { reportingCurrency: value } }),
    onSuccess: () => ['reporting-currency', 'me', 'risk', 'my-totals'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="m-0 text-[15px] font-semibold">Reporting currency</h2>
      <Field label="Show floor totals in" hint="The risk desk and the totals on Today open in this currency. You can still switch on each screen.">
        <Select value={value} onChange={(e) => setPick(e.target.value)}>
          {cur.data?.currencies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Field>
      <ErrorText error={save.error ? errorText(save.error) : null} />
      {save.isSuccess && <p className="m-0 text-sm text-up">Saved.</p>}
      <Button variant="primary" className="self-start" onClick={() => save.mutate()} disabled={save.isPending || !value}>
        Save currency
      </Button>
    </Card>
  );
}
